import { PARAMETER_DEFINITIONS, STATE_VARIABLE_DEFINITIONS } from "./baseTemplate.ts";
import { NO_MODEL, nextId, updateSubBasins } from "./entityHelpers.ts";
import type { EvalHidProject, Zone } from "./types.ts";
import { TranslatableError } from "../i18n/translate.ts";

/** Adds a zone and generates its model's default parameters/state variables for it (like Zonas.vb does on creation) — same verb as addAquifer/addOutletPoint, the equivalent single-entity-append function for the other 2 entity types. */
export function addZone(
  project: EvalHidProject,
  name: string,
  modelTypeCode: number,
  kind: number
): EvalHidProject {
  const cleanName = name.trim();
  if (!cleanName) throw new TranslatableError("projectEntities.emptyZoneName");
  if (
    project.zones.some(
      (z) =>
        z.name === cleanName &&
        (z.modelTypeCode === (kind === 0 ? modelTypeCode : null) ||
          z.snowModelTypeCode === (kind === 1 ? modelTypeCode : null))
    )
  ) {
    throw new TranslatableError("projectEntities.zoneAlreadyExists", { name: cleanName });
  }

  const zoneCode = nextId(project.zones, (z) => z.zoneCode);
  const zone: Zone = {
    zoneCode,
    name: cleanName,
    modelTypeCode: kind === 0 ? modelTypeCode : null,
    snowModelTypeCode: kind === 1 ? modelTypeCode : null,
  };

  const newParameters = PARAMETER_DEFINITIONS.filter(
    (d) => d.modelTypeCode === modelTypeCode && d.kind === kind
  ).map((d) => ({
    zoneCode,
    parameterCode: d.parameterCode,
    kind,
    value: d.defaultValue,
  }));

  const newStateVariables = STATE_VARIABLE_DEFINITIONS.filter(
    (d) => d.modelTypeCode === modelTypeCode && d.kind === kind
  ).map((d) => ({
    zoneCode,
    stateVariableCode: d.stateVariableCode,
    kind,
    value: d.defaultValue,
  }));

  return {
    ...project,
    zones: [...project.zones, zone],
    parameterValues: [...project.parameterValues, ...newParameters],
    stateVariableValues: [...project.stateVariableValues, ...newStateVariables],
  };
}

/** Renames an already-created zone. */
export function renameZone(
  project: EvalHidProject,
  zoneCode: number,
  name: string
): EvalHidProject {
  const cleanName = name.trim();
  if (!cleanName) throw new TranslatableError("projectEntities.emptyZoneName");
  return {
    ...project,
    zones: project.zones.map((z) => (z.zoneCode === zoneCode ? { ...z, name: cleanName } : z)),
  };
}

/**
 * Changes the model of an already-created zone — only the model WITHIN the
 * kind it already has (rainfall-runoff or snow, whichever of the two fields
 * is assigned); a zone never changes kind, that requires creating another
 * one. Like adding the zone (`addZone`), it replaces its parameters
 * and state variables with the new model's default values — any hand-edited
 * value is lost, so it does nothing if the chosen model is the same one it
 * already had (avoids losing edits from an accidental re-selection of the
 * dropdown). It also re-syncs the denormalized model
 * (`SubBasin.modelTypeCode`/`snowModelTypeCode`, see `assignZoneInBulk`) of
 * every sub-basin that already had this zone assigned — otherwise
 * `engine.ts` (which reads that field directly from the sub-basin, not from
 * the zone) would keep calculating with the old model.
 */
export function changeZoneModel(
  project: EvalHidProject,
  zoneCode: number,
  newModelTypeCode: number
): EvalHidProject {
  const zone = project.zones.find((z) => z.zoneCode === zoneCode);
  if (!zone) throw new TranslatableError("projectEntities.zoneDoesNotExist");
  const kind = zone.modelTypeCode != null ? 0 : 1;
  const currentModel = kind === 0 ? zone.modelTypeCode : zone.snowModelTypeCode;
  if (currentModel === newModelTypeCode) return project;

  const modelField = kind === 0 ? "modelTypeCode" : "snowModelTypeCode";
  const zoneField = kind === 0 ? "zoneCode" : "snowZoneCode";

  const newParameters = PARAMETER_DEFINITIONS.filter(
    (d) => d.modelTypeCode === newModelTypeCode && d.kind === kind
  ).map((d) => ({
    zoneCode,
    parameterCode: d.parameterCode,
    kind,
    value: d.defaultValue,
  }));
  const newStateVariables = STATE_VARIABLE_DEFINITIONS.filter(
    (d) => d.modelTypeCode === newModelTypeCode && d.kind === kind
  ).map((d) => ({
    zoneCode,
    stateVariableCode: d.stateVariableCode,
    kind,
    value: d.defaultValue,
  }));

  return {
    ...project,
    zones: project.zones.map((z) =>
      z.zoneCode === zoneCode ? { ...z, [modelField]: newModelTypeCode } : z
    ),
    parameterValues: [
      ...project.parameterValues.filter((v) => v.zoneCode !== zoneCode),
      ...newParameters,
    ],
    stateVariableValues: [
      ...project.stateVariableValues.filter((v) => v.zoneCode !== zoneCode),
      ...newStateVariables,
    ],
    subBasins: project.subBasins.map((c) =>
      c[zoneField] === zoneCode ? { ...c, [modelField]: newModelTypeCode } : c
    ),
  };
}

/**
 * Permanent delete (like Zonas.vb/BT_Delete_Click in the original): removes
 * the zone and its parameters/state variables, and cascades (to null) the
 * unassignment on any sub-basin that had it as its zone or snow zone —
 * never leaves them pointing at a zone that no longer exists. No undo: to
 * recover it you have to recreate it and reassign it by hand.
 */
export function deleteZone(project: EvalHidProject, zoneCode: number): EvalHidProject {
  return {
    ...project,
    zones: project.zones.filter((z) => z.zoneCode !== zoneCode),
    parameterValues: project.parameterValues.filter((v) => v.zoneCode !== zoneCode),
    stateVariableValues: project.stateVariableValues.filter((v) => v.zoneCode !== zoneCode),
    subBasins: project.subBasins.map((c) => ({
      ...c,
      zoneCode: c.zoneCode === zoneCode ? null : c.zoneCode,
      snowZoneCode: c.snowZoneCode === zoneCode ? null : c.snowZoneCode,
      modelTypeCode: c.zoneCode === zoneCode ? NO_MODEL : c.modelTypeCode,
      snowModelTypeCode: c.snowZoneCode === zoneCode ? NO_MODEL : c.snowModelTypeCode,
    })),
  };
}

/** Changes a zone's parameter value (manual edit after creating it with its default values). */
export function updateParameterValue(
  project: EvalHidProject,
  zoneCode: number,
  parameterCode: number,
  kind: number,
  value: number
): EvalHidProject {
  return {
    ...project,
    parameterValues: project.parameterValues.map((v) =>
      v.zoneCode === zoneCode && v.parameterCode === parameterCode && v.kind === kind
        ? { ...v, value: value }
        : v
    ),
  };
}

/** Changes a zone's initial state variable value (manual edit after creating it with its default values). */
export function updateStateVariableValue(
  project: EvalHidProject,
  zoneCode: number,
  stateVariableCode: number,
  kind: number,
  value: number
): EvalHidProject {
  return {
    ...project,
    stateVariableValues: project.stateVariableValues.map((v) =>
      v.zoneCode === zoneCode && v.stateVariableCode === stateVariableCode && v.kind === kind
        ? { ...v, value: value }
        : v
    ),
  };
}

/**
 * Assigns a zone (or removes it, with zoneCode=null) to several sub-basins
 * at once (like AsignacionZonas.vb/AsigZona.vb). The sub-basin's model
 * (modelTypeCode/snowModelTypeCode) is set automatically to the zone's
 * model — it's the only way to assign a model, there's no separate step.
 */
export function assignZoneInBulk(
  project: EvalHidProject,
  subBasinCodes: ReadonlySet<number>,
  field: "zoneCode" | "snowZoneCode",
  zoneCode: number | null
): EvalHidProject {
  const modelField = field === "zoneCode" ? "modelTypeCode" : "snowModelTypeCode";
  const zone = zoneCode != null ? project.zones.find((z) => z.zoneCode === zoneCode) : null;
  const modelCode =
    zoneCode == null
      ? NO_MODEL
      : ((field === "zoneCode" ? zone?.modelTypeCode : zone?.snowModelTypeCode) ?? NO_MODEL);

  return updateSubBasins(project, subBasinCodes, (c) => ({
    ...c,
    [field]: zoneCode,
    [modelField]: modelCode,
  }));
}
